/**
 * GA/GTAG tracking — same steps as main project (lead_open, lead_form_step_1_submit, etc.).
 * In dev mode, intercepts events and shows them in DevOverlay instead of sending.
 */
import {
  initGtag,
  trackLeadOpen,
  trackLeadSubmitAttempt,
  trackLeadSubmitSuccess,
  trackLeadSubmitError,
  trackLeadFormStepSubmit,
  trackLeadFormConversion,
} from '../plugins/gtag'
import type { ContactMeOpenDetail } from '../types'

function getPropertyIdFromContext(context?: ContactMeOpenDetail['context']): string | undefined {
  if (!context || typeof context !== 'object') return undefined
  const id =
    (context as Record<string, unknown>).propertyId ??
    (context as Record<string, unknown>).property_id
  return id != null ? String(id) : undefined
}

function getDevOverlay() {
  return import.meta.env.DEV ? (window as any).__bostDevOverlay : null
}

export function useContactMeAnalytics() {
  const init = (gtagId?: string) => {
    if (gtagId) initGtag(gtagId)
  }

  const trackOpen = (detail?: ContactMeOpenDetail) => {
    const propertyId = getPropertyIdFromContext(detail?.context)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_open', 'contact', propertyId)
    } else {
      trackLeadOpen(propertyId)
    }
  }

  const trackStep1Submit = (detail?: ContactMeOpenDetail) => {
    const propertyId = getPropertyIdFromContext(detail?.context)
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent(
        'lead_form_step_1_submit',
        'lead_form',
        propertyId ? `property:${propertyId}` : undefined
      )
    } else {
      trackLeadFormStepSubmit(1, { propertyId })
    }
  }

  const trackStep2Submit = (propertyId?: string) => {
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent(
        'lead_form_step_2_submit',
        'lead_form',
        propertyId ? `property:${propertyId}` : undefined
      )
    } else {
      trackLeadFormStepSubmit(2, { propertyId })
    }
  }

  const trackSubmitAttempt = (propertyId?: string) => {
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_submit_attempt', 'contact', propertyId)
    } else {
      trackLeadSubmitAttempt(propertyId)
    }
  }

  const trackSubmitSuccess = (propertyId?: string) => {
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_submit_success', 'contact', propertyId)
    } else {
      trackLeadSubmitSuccess(propertyId)
    }
  }

  const trackSubmitError = (ctx: { propertyId?: string; code?: string }) => {
    const label = [
      ctx?.propertyId ? `prop=${ctx.propertyId}` : null,
      ctx?.code ? `code=${ctx.code}` : null,
    ]
      .filter(Boolean)
      .join(';')
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_submit_error', 'contact', label || undefined)
    } else {
      trackLeadSubmitError(ctx)
    }
  }

  const trackConversion = (ctx: { conversationId?: string; propertyId?: string }) => {
    const devOverlay = getDevOverlay()
    if (devOverlay) {
      devOverlay.addEvent('lead_form_conversion', 'lead_form', ctx.conversationId)
    } else {
      trackLeadFormConversion(ctx)
    }
  }

  return {
    init,
    trackOpen,
    trackStep1Submit,
    trackStep2Submit,
    trackSubmitAttempt,
    trackSubmitSuccess,
    trackSubmitError,
    trackConversion,
  }
}
